<template>
    <div v-if="products.length" ref="carousel" class="slim-carousel">
        <a
            v-if="products.length > minProductsArrowSliderCount"
            href="#"
            class="slim-carousel__control slim-carousel__control--left js-slime-carousel-left"
            :class="typeNavigation"
            :aria-label="$t('slides.previous')"
            tabindex="-1"
        >
            <img :src="`/nuxt-assets/icons/arrow-black.svg`" alt="arrow" width="11" height="20" />
        </a>
        <a
            v-if="products.length > minProductsArrowSliderCount"
            href="#"
            class="slim-carousel__control slim-carousel__control--right js-slime-carousel-right"
            :class="typeNavigation"
            :aria-label="$t('slides.next')"
            tabindex="-1"
        >
            <img :src="`/nuxt-assets/icons/arrow-black.svg`" alt="arrow" width="11" height="20" />
        </a>
        <div class="slim-carousel__container js-slim-carousel-scroll-container elements-carousel-container">
            <div v-for="(item, index) in products" :key="index" class="slim-carousel__item" :class="typeItem" data-cy="slim-carousel__item">
                <ObjectsProduct
                    :item="item"
                    :is-carousel="true"
                    :show-pre-basket="showPreBasket"
                    :item-list-name="itemListName"
                    :carousel-name="carouselName"
                    :list-position="index"
                    :custom-button-title="customButtonTitle"
                    :minimalized-product-visual="minimalizedProductVisual"
                    :show-availability="showAvailability"
                    :addon-sale-behavior="addonSaleBehavior"
                    :show-pharmacy-price="showPharmacyPrice"
                    @custom-button-clicked="$emit('customButtonClicked', $event)"
                    @product-redirected-to-url="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Click)"
                    @product-added-to-basket="(productUrl) => sendLuigisboxEvent(productUrl, LuigisBoxClickEventTypeEnum.Conversion)"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import SlimCarousel from '~/mixins/SlimCarousel'
import Product from '~/models/Product/Product'
import LuigisBoxEventApi from '~/extensions/LuigisBoxEventApi'
import { LuigisBoxClickEventTypeEnum } from '~/models/LuigisBox/LuigisBoxClickEventTypeEnum'

export default {
    components: {},
    props: {
        products: {
            type: Array as PropType<Product[]>,
            default: () => [],
        },
        typeItem: {
            type: String,
            default: 'slim-carousel__item--product',
        },
        typeNavigation: {
            type: String,
            default: null,
        },
        showPreBasket: {
            type: Boolean,
            default: true,
        },
        itemListName: {
            type: String,
            default: '',
        },
        carouselName: {
            type: String,
            default: '',
        },
        customButtonTitle: {
            type: String,
            default: null,
            required: false,
        },
        minimalizedProductVisual: {
            type: Boolean,
            default: false,
        },
        minProductsArrowSliderCount: {
            type: Number,
            default: 5,
        },
        showAvailability: {
            type: Boolean,
            default: true,
        },
        addonSaleBehavior: {
            type: Boolean,
            default: false,
        },
        showPharmacyPrice: {
            type: Boolean,
            default: true,
        },
    },

    emits: ['customButtonClicked'],

    computed: {
        LuigisBoxClickEventTypeEnum() {
            return LuigisBoxClickEventTypeEnum
        },
    },

    updated() {
        if (this.$refs.carousel) {
            if (
                this.$refs.carousel.getElementsByClassName('product').length ||
                this.$refs.carousel.getElementsByClassName('product-card__container').length
            ) {
                new SlimCarousel({ el: this.$refs.carousel })
            }
        }
    },

    mounted() {
        if (this.$refs.carousel) {
            if (
                this.$refs.carousel.getElementsByClassName('product').length ||
                this.$refs.carousel.getElementsByClassName('product-card__container').length
            ) {
                new SlimCarousel({ el: this.$refs.carousel })
            }
        }
    },

    methods: {
        sendLuigisboxEvent(productUrl: string, type: LuigisBoxClickEventTypeEnum) {
            LuigisBoxEventApi.sendClickEvent(type, productUrl)
        },
    },
}
</script>

<style lang="scss" scoped>
/*
Structure:
    .slim-carousel(.slim-carousel--fullWidth)
        .slim-carousel__control(.slim-carousel__control--left|slim-carousel__control--right|slim-carousel__control--outside)
        .slim-carousel__container
            .slim-carousel__item(.slim-carousel__item--product|.slim-carousel__item--detail)
*/

.slim-carousel {
    position: relative;
    container-type: inline-size;

    @media (min-width: 480px) {
        margin-bottom: 0.5rem;
    }

    &--fullWidth {
        width: calc(100% + (2 * $padding-container-inside-mobile));
        margin-left: -$padding-container-inside-mobile;

        @media only screen and (min-width: 576px) {
            width: calc(100% + (2 * $padding-container-inside));
            margin-left: -$padding-container-inside;
        }

        @media (min-width: $max-width-container-inside) {
            width: calc(100% + 12px); //margin on .product
            margin-left: -6px;
        }
    }
}

.slim-carousel__control {
    display: none;
    padding-left: 3px;

    @media (min-width: 890px) {
        position: absolute;
        top: calc(50% - 42px);
        width: 33px;
        height: 33px;
        border-radius: 33px;
        transition: all 0.3s;
        z-index: 5;
        background: $color-white;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid $color-secondary-dark-green-2;
    }

    &--left {
        @media (min-width: 890px) {
            left: 0;
            transform: rotate(180deg);
        }
    }

    &--right {
        @media (min-width: 890px) {
            right: 0;
        }
    }

    &--outside {
        &.slim-carousel__control--left {
            @media (min-width: 890px) {
                left: -16px;
            }
        }

        &.slim-carousel__control--right {
            @media (min-width: 890px) {
                right: -16px;
            }
        }
    }

    &:deep(svg) {
        margin-left: 4px;
        fill: $color-white;
    }
}

.slim-carousel__container.elements-carousel-container {
    display: flex;
    padding: 0px 0px 0px 10px;
    height: auto;
    overflow: auto hidden;
    width: 100%;
    scrollbar-width: none;
    -ms-overflow-style: none;
    scroll-snap-type: x mandatory;

    @media (min-width: $max-width-container-inside) {
        padding: 0 0 5px;
    }

    &::-webkit-scrollbar {
        -webkit-appearance: none;
        height: 10px;
    }

    &:hover::-webkit-scrollbar-thumb {
        border: 0;
        background-color: $color-grey-semi-dark;
        border-radius: 10px;
        cursor: pointer;

        @media (min-width: 890px) {
            display: none;
        }
    }
}

.slim-carousel__item {
    &--product {
        display: flex;
        flex: 0 0 calc(100% / 2.1);
        width: calc(100% / 2.1);

        //@media (min-width: 460px) {
        //    flex: 0 0 calc(100% / 2.5);
        //    width: calc(100% / 2.5);
        //}

        @container (min-width: 460px) {
            flex: 0 0 calc(100% / 3.5);
            width: calc(100% / 3.5);
        }

        @container (min-width: 660px) {
            flex: 0 0 calc(100% / 4.5);
            width: calc(100% / 4.5);
        }

        @container (min-width: 960px) {
            flex: 0 0 calc(100% / 5.5);
            width: calc(100% / 5.5);
        }
    }

    &--addon {
        display: flex;
        flex: 0 0 calc(100% / 2.25);
        width: calc(100% / 2.25);

        //@include screen-sm {
        //    flex: 0 0 calc(100% / 2.5);
        //    width: calc(100% / 2.5);
        //}

        @include screen-xl {
            flex: 0 0 calc(100% / 4.5);
            width: calc(100% / 4.5);
        }
    }

    &--detail {
        display: flex;
        flex: 0 0 calc(100% / 2.25);

        @media (min-width: 390px) {
            flex: 0 0 calc(100% / 2.25);
        }
        @media (min-width: 490px) {
            flex: 0 0 calc(100% / 3.5);
        }
    }

    &--prebasket {
        display: flex;
        flex: 0 0 calc(100% / 1.75);

        @media (min-width: 490px) {
            flex: 0 0 calc(100% / 4.5);
        }
    }

    &--two {
        display: flex;
        flex: 0 0 calc(100% / 2.5);
    }
}
</style>
